import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { addMessage, addUserMessage, setAIIconStatus } from '../../redux/reducers/aiChatSlice';
import { addSupportMessage, addSupportUserMessage, setSupportAIIconStatus } from '../../redux/reducers/supportChatSlice';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { StateType } from '../../Models/StateTypes';
import { AIChatConfig, advisorConfig } from './chatConfig';

const useStyles = makeStyles((theme) => ({
  presetQuestions: {
    padding: '9px 16px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.type === 'dark' ? (theme as any).custom.surface : '#ffffff',
  },
  questionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  button: {
    borderRadius: '20px',
    textTransform: 'none',
    fontSize: '0.95rem',
    justifyContent: 'flex-start',
    color: theme.palette.type === 'dark' ? (theme as any).custom.textSecondary : theme.palette.text.secondary,
    borderColor: theme.palette.type === 'dark' ? (theme as any).custom.border : undefined,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      backgroundColor: theme.palette.type === 'dark' ? (theme as any).custom.surfaceElevated : 'white',
    },
  },
}));

interface PresetQuestionsProps {
  config?: AIChatConfig;
}

const PresetQuestions: React.FC<PresetQuestionsProps> = ({ config = advisorConfig }) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isSupport = config.reduxSliceName === 'supportChat';
  const { messages, aiIconStatus } = useSelector((state: StateType) =>
    isSupport ? state.supportChat : state.aiChat
  );

  const PRESET_QUESTIONS = config.presetQuestionKeys.map((key) => t(key));

  const handlePresetClick = (question: string) => {
    if (isSupport) {
      dispatch(addSupportUserMessage({
        MessageID: uuidv4(),
        MessageTimestamp: new Date().toISOString(),
        MessageTypeID: 1,
        ResponseTimeMs: null,
        MessageText: question,
      }));
      dispatch(addSupportMessage({ MessageText: question, MessageTypeID: 1 }));
      dispatch(setSupportAIIconStatus(1));
    } else {
      dispatch(addUserMessage({
        MessageID: uuidv4(),
        MessageTimestamp: new Date().toISOString(),
        MessageTypeID: 1,
        ResponseTimeMs: null,
        MessageText: question,
      }));
      dispatch(addMessage({ MessageText: question, MessageTypeID: 1 }));
      dispatch(setAIIconStatus(1));
    }
  };

  const userMessages = messages.filter(msg => msg.MessageTypeID === 1);

  if (userMessages.length > 0) {
    return null;
  }

  return (
    <Box className={classes.presetQuestions}>
      <Typography
        variant="body1"
        color="textSecondary"
        style={theme.palette.type === 'dark' ? { color: (theme as any).custom.textSecondary } : undefined}
      >
        {t("common.orTryOneOfThese")}
      </Typography>
      <Box className={classes.questionsContainer}>
        {PRESET_QUESTIONS.map((q: string, index: number) => (
          <Button
            key={index}
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={() => handlePresetClick(q)}
            disabled={aiIconStatus === 1}
          >
            {q}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default PresetQuestions;
